import React, { useId } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import AdvancedChartLegend from '../AdvancedChartLegend';
import SmartChart from '../../../../../components/Chart/SmartChart';
import Skeleton from '../../../../../components/Skeleton';
import useAdvancedLegend from '../../hooks/useAdvancedLegend';

import { transitionsChartConfig } from './config';
import { buildAdvancedLegendItems } from '../../utils/build-advanced-legend-items';

const TransitionsChart = ({
  data,
  nodes,
  exitNodeNames,
  selectedDate,
  withEndSeries = false,
  oneToMany = false,
  colorLinksByDestination = false,
  settings,
  footerRight,
  isPress,
}) => {
  const initSeries = data?.chart_series || [];

  const chartId = useId();
  const { activeItem, hoveredItem, onClick, onMouseOver, onMouseOut, classesToAdd } =
    useAdvancedLegend();

  const sourceIds = new Set(initSeries.map((item) => item[0]));
  const destinationIds = new Set(initSeries.map((item) => item[1]));

  const legendNodes = nodes.filter((node) => {
    if (oneToMany) return node.id.includes('End-') && destinationIds.has(node.id);
    if (withEndSeries && exitNodeNames?.has(node.name) && destinationIds.has(node.name))
      return true;
    if (sourceIds.has(node.id)) return true;
    if (node.name.includes('End-')) return false;
    return false;
  });

  const legendNodeIds = legendNodes.map((node) => node.id);

  const hoveredLegendNode = legendNodes.find((node) => node.id === hoveredItem?.id);

  const activeNode = legendNodes.find(
    (node) => node.id === activeItem?.id || node.id === hoveredItem?.id
  );

  const shouldAddClass = !!activeNode || !!hoveredLegendNode;

  const nextNodes = nodes.map((node) => {
    const classNameKey =
      oneToMany && !node.id.includes('End-')
        ? activeItem?.id || hoveredItem?.id || node.id
        : node.id;
    const className = classesToAdd(classNameKey);
    return {
      ...node,
      className: `
        ${shouldAddClass ? className : ''}
        ${node.id.includes('End-') ? 'to-node' : ''}
        ${activeItem?.id?.includes('End-') && !node.id.includes('End-') ? 'from-node' : ''}`,
    };
  });

  const nodeColorById = new Map(nodes.map((node) => [node.id, node.color]));

  const nextSeries = initSeries?.map((item) => {
    const className = classesToAdd(
      (!activeItem?.id && hoveredItem?.id?.includes('End-')) || activeItem?.id?.includes('End-')
        ? item[1]
        : item[0]
    );

    const link = [...item, shouldAddClass ? className : ''];

    // Color the flow by its destination (End-) node instead of the default from-node color.
    return colorLinksByDestination ? [...link, nodeColorById.get(item[1])] : link;
  });

  const config = transitionsChartConfig({
    selectedDate,
    legendNodeIds,
    activeItem,
    hoveredItem,
    hasData: !!initSeries.length,
    onClick,
    onMouseOver,
    oneToMany,
    isPress,
  });

  const metaSeriesData = {
    sankey: {
      type: 'sankey',
      borderRadius: 0,
      nodeWidth: 16,
      colorByPoint: true,
      clip: false,
      visible: true,
      x: -8,
      keys: colorLinksByDestination
        ? ['from', 'to', 'weight', 'className', 'color']
        : ['from', 'to', 'weight', 'className'],
      nodes: nextNodes,
    },
  };

  const legendOptions = buildAdvancedLegendItems(legendNodes);

  if (settings.pending) return <Skeleton height={392} />;

  return (
    <div
      className={cx('chart-container transitions-chart', {
        'has-active-item': activeItem?.id,
        'has-hovered-item': hoveredItem?.id && !activeItem?.id,
      })}
    >
      <div onMouseOut={onMouseOut} onBlur={onMouseOut}>
        <SmartChart
          chartId={`transitions-chart--${chartId}`}
          config={config}
          series={{ sankey: nextSeries }}
          metaSeriesData={metaSeriesData}
        />
      </div>
      <div className="d-flex align-items-center justify-content-between gap-16">
        <AdvancedChartLegend
          items={legendOptions}
          activeItem={
            activeItem?.id || (!activeItem?.id && !hoveredItem?.to ? hoveredItem?.id : undefined)
          }
          onClick={onClick}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
        />
        {footerRight}
      </div>
    </div>
  );
};

TransitionsChart.propTypes = {
  settings: T.shape({
    pending: T.bool,
  }).isRequired,
  data: T.shape({
    chart_series: T.array,
  }).isRequired,
  nodes: T.arrayOf(
    T.shape({
      id: T.string.isRequired,
      name: T.string.isRequired,
      color: T.string,
    })
  ).isRequired,
  exitNodeNames: T.instanceOf(Set),
  selectedDate: T.string.isRequired,
  withEndSeries: T.bool,
  oneToMany: T.bool,
  colorLinksByDestination: T.bool,
  footerRight: T.node,
  isPress: T.bool,
};

export default TransitionsChart;
